<template>
  <div
    v-if="appointment"
    class="header-container flex-col md:flex-row gap-4 md:divide-x *:pl-3"
  >
    <div class="customer-container">
      <div class="d-flex mb-2">
        <Link
          :to="userShowPath(appointment.customer.customer_id)"
          :text="customerName"
        />
        <img
          v-if="customer.membership"
          :src="customer.membership.icon_url"
          alt="Membership Icon"
          class="ml-1 member-icon"
        />
      </div>
      <div class="text-title">{{ appointment.address.name }}</div>
      <address-label :address="appointment.address" class="text-body" />
      <div class="text-body">{{ customerEmail }}</div>
      <div class="text-body">
        <i class="far fa-lg fa-phone-flip mr-1"></i>
        {{ customerPhone }}
      </div>
    </div>
    <div class="appointment-container">
      <div class="mb-2">
        <Link
          :to="redirectToAppointment(appointment.job.id, appointment.id)"
          :text="`Appointment #${appointment.id}`"
          class="whitespace-nowrap"
        />
      </div>
      <div class="text-title">{{ serviceCategoryName }}</div>
      <PriorityLabel :priority="priority" />
      <div class="whitespace-nowrap">
        <span class="text-title">Scheduled for:</span>
        <span class="text-body">
          {{ formatScheduledTime }}
        </span>
      </div>
      <div class="d-flex">
        <span class="text-title">Duration:</span>
        <minutes-label
          class="ml-1 text-body"
          :minutes="
            appointment.duration_minutes ||
            appointment.estimated_duration_minutes
          "
        ></minutes-label>
      </div>
      <div>
        <span class="text-title mr-1">Created By:</span>
        <span class="text-body">
          <i v-if="appointment.customer_scheduled" class="fas fa-user mr-1"></i>
          <template v-if="appointment.created_by">
            <a :href="redirectToCreatedBy">
              {{ appointment.created_by.full_name }}
            </a>
          </template>
          <template v-else>
            {{ bookedBy }}
          </template>
        </span>
      </div>
    </div>
    <div class="job-container">
      <div class="mb-2">
        <Link
          :to="redirectToJob(appointment.job.id)"
          :text="`Job #${appointment.job.id}`"
        />
      </div>
      <div class="whitespace-nowrap">
        <span class="text-title">Appointments:</span>
        <span class="text-body ml-1">{{ appointmentNumber }}</span>
      </div>
      <div class="text-body">{{ jobDepartmentName }}</div>
      <div>
        <span class="text-title">Permit:</span>
        <span class="text-body">{{ permitValue }}</span>
      </div>
    </div>
    <div class="technician-container">
      <div v-if="assignedTechnicians.length > 0">
        <div class="whitespace-nowrap mb-1">Assigned to:</div>
        <div
          v-for="tech in assignedTechnicians"
          :key="tech.technician_id || tech.user_id"
          class="d-flex mb-2"
        >
          <div class="avatar-container mr-2">
            <div
              v-if="tech.avatar && tech.avatar.url"
              class="tech-avatar"
              :style="{
                'background-image': `url(${tech.avatar.url})`,
                'background-position': 'center'
              }"
            />
            <Avatar
              v-else
              :id="tech.technician_id"
              class="tech-avatar"
              :content="getInitials(tech)"
              color="#014451"
            />
          </div>
          <div class="technician-details">
            <div class="d-flex">
              <tooltip
                v-if="isTechPinned && isPrimaryTech(tech)"
                placement="top"
              >
                <template #trigger>
                  <i class="fas fa-lock mr-1"></i>
                </template>
                <template #tooltip>
                  <div style="max-width: 350px">
                    <span v-if="pinnedReason">
                      <b>Pinned Reason:</b>
                      {{ pinnedReason }}
                    </span>
                    <span v-else>Pinned</span>
                  </div>
                </template>
              </tooltip>
              <Link
                :to="technicianShowPath(tech.user_id)"
                :text="tech.full_name || '--'"
                class="whitespace-nowrap"
              />
            </div>
            <div class="whitespace-nowrap">
              <i class="far fa-lg fa-phone-flip mr-1"></i
              >{{ tech.phone || "--" }}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="d-flex align-items-center">
        <Avatar id="unknown" class="tech-avatar" color="#014451" />
        <div>
          <div class="whitespace-nowrap">Assigned To:</div>
          <div>Unassigned</div>
        </div>
      </div>
      <CrewMembersList :crew-members="appointment.crew_members" />
    </div>
  </div>
</template>

<script>
import { getProperty } from "@/lib/helpers.js";

import AddressLabel from "@/components/AddressLabel.vue";
import Avatar from "@/components/Avatar.vue";
import Divider from "@/components/Divider.vue";
import Link from "@/components/Link.vue";
import MinutesLabel from "@/components/MinutesLabel.vue";
import PriorityLabel from "@/components/PriorityLabel.vue";
import Tooltip from "@/components/Tooltip.vue";
import CrewMembersList from "@/Job/CrewMembersList.vue";
import {
  jobShowPath,
  polymorphicUserPath,
  technicianShowPath,
  userShowPath
} from "@/mixins/path_helpers";

export default {
  components: {
    Link,
    Avatar,
    Divider,
    PriorityLabel,
    AddressLabel,
    CrewMembersList,
    MinutesLabel,
    Tooltip
  },
  props: {
    appointment: {
      type: Object,
      default: null
    }
  },
  computed: {
    customer() {
      return this.appointment.customer;
    },
    customerName() {
      const { full_name } = this.customer;
      return full_name || "--";
    },
    customerPhone() {
      const { phone } = this.customer;
      return phone || "--";
    },
    customerEmail() {
      const { email } = this.customer;
      return email || "--";
    },
    bookedBy() {
      const { customer_scheduled } = this.appointment;
      return customer_scheduled ? "Customer" : "Staff";
    },
    serviceCategoryName() {
      const { service_category } = this.appointment;
      return getProperty(service_category, "name") || "--";
    },
    priority() {
      const { service_category } = this.appointment;
      return getProperty(service_category, "priority");
    },
    formatScheduledTime() {
      return this.scheduledDateTime(
        this.appointment.scheduled_time,
        this.appointment.arrival_window_start,
        this.appointment.arrival_window_end,
        false
      );
    },
    assignedTechnicians() {
      return this.resolveAssignedTechnicians(this.appointment);
    },
    isTechPinned() {
      return this.appointment.assignment_pinned;
    },
    pinnedReason() {
      return this.appointment.pinned_reason || "N/A";
    },
    appointmentNumber() {
      const { job } = this.appointment;
      return getProperty(job, "appointment_count");
    },
    jobDepartmentName() {
      const { job } = this.appointment;
      return getProperty(getProperty(job, "department"), "name");
    },
    permitValue() {
      const { job } = this.appointment;
      //only one permit per job right now
      return job.permits.length
        ? this.capitalize(job.permits[0].status)
        : " N/A";
    },
    redirectToCreatedBy() {
      return polymorphicUserPath({
        id: this.appointment.created_by.id,
        type: this.appointment.created_by.user_type
      });
    }
  },
  methods: {
    userShowPath,
    technicianShowPath,
    isPrimaryTech(tech) {
      const primary = this.appointment.technician;
      if (!primary) return false;
      return tech.user_id === primary.user_id;
    },
    redirectToJob(jobId) {
      return jobShowPath(jobId);
    },
    redirectToAppointment(jobId, appointmentId) {
      return jobShowPath(jobId, appointmentId);
    }
  }
};
</script>

<style lang="scss" scoped>
.header-container {
  display: flex;
  margin-bottom: 1rem;
  overflow: auto;
  padding: 15px 10px;
  border: 1px solid var(--gray-border-color);
  background: var(--white);
  border-radius: 4px;
  line-height: 1.67;
}

.member-icon {
  height: 1em;
}

.tech-avatar {
  min-width: 45px;
  width: 45px;
  height: 45px;
  background-size: cover;
  border-radius: 50%;
}

.technician-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.customer-container,
.appointment-container,
.technician-container {
  flex: 1;
}

.customer-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.appointment-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.job-container {
  flex: none;
  width: 16%;
}

.technician-container {
  display: flex;
  flex-direction: column;
  min-width: 25%;
  flex-grow: 2;
}

.avatar-container {
  display: flex;
  align-items: center;
}

.address-label {
  display: flex;
  align-items: baseline;
}

a {
  font-weight: bold;
}
</style>
